import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Grid } from '@mui/material'
import React, { useCallback, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { SettingsBox } from 'src/Elements/Data'
import SettingsFormContainer from 'src/Libs/form/components/SettingsFormContainer'
import { NumberField, Toggle } from 'src/Libs/inputs/react-hook-form'
import useGridAndSystemProtectionMutation from '../hooks/use-grid-and-system-protection-mutation'
import useSyncFormWithValues from '../hooks/use-sync-form-with-values'
import { FUNCTION_NUMBER } from '../types/common'
import { DeadBandThresholdSettingsModel, DynamicGridRegenerationSettingsModel } from '../types/grid-and-system-protection.model'
import { DynamicGridRegenerationSettingsForm, getDynamicGridRegenerationSettingsSchema } from '../types/schemas'
import { getChangedFieldStyle, isFieldDisabled } from '../utils'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { useFarmContext } from 'src/Elements/Farm'
const NAME_SPACE = 'systemSettings.grid-and-system-protection-configuration.dynamic-grid-regeneration'

type Props = {
  dynamicGridRegenerationSettingsModel: DynamicGridRegenerationSettingsModel
  deadBandThresholdSettingsModel: DeadBandThresholdSettingsModel
}

const DynamicGridRegenerationSettings: React.FC<Props> = ({ dynamicGridRegenerationSettingsModel, deadBandThresholdSettingsModel }) => {
  const { t } = useCustomTranslation()
  const { isFarm, isFarmMasterSelected } = useFarmContext()
  const { hasInstallationWritingRights } = useInstallationAccessRightsContext()

  const canEdit = isFarm ? hasInstallationWritingRights && isFarmMasterSelected : hasInstallationWritingRights
  const defaultValues = useMemo(() => getDefaultValues(dynamicGridRegenerationSettingsModel), [dynamicGridRegenerationSettingsModel])
  const methods = useForm<DynamicGridRegenerationSettingsForm>({
    resolver: zodResolver(getDynamicGridRegenerationSettingsSchema(dynamicGridRegenerationSettingsModel)),
    defaultValues,
    mode: 'onBlur',
  })
  useSyncFormWithValues(methods.reset, defaultValues)

  const { isLoading, updateGridAndSystemProtection } = useGridAndSystemProtectionMutation()

  const onSubmit = useCallback(
    async (data: DynamicGridRegenerationSettingsForm): Promise<void> => {
      try {
        await updateGridAndSystemProtection(
          {
            dynamicGridRegenerationSettings: data,
            // ? since BE doesn't accept partial DynamicGridPower payload we need to send deadBandThresholdSettings as well
            deadBandThresholdSettings: {
              zeroCurrentMode: {
                lower: deadBandThresholdSettingsModel.zeroCurrentMode.lower.value,
                upper: deadBandThresholdSettingsModel.zeroCurrentMode.upper.value,
              },
              reactiveCurrentControl: {
                lower: deadBandThresholdSettingsModel.reactiveCurrentControl.lower.value,
                upper: deadBandThresholdSettingsModel.reactiveCurrentControl.upper.value,
              },
            },
          },
          FUNCTION_NUMBER.dynamicGridPowerSettings
        )
        methods.reset(data)
      } catch (error) {
        methods.reset()
      }
    },
    [methods, updateGridAndSystemProtection, deadBandThresholdSettingsModel]
  )

  return (
    <FormProvider {...methods}>
      <Grid item xs={12}>
        <AccordionItem title={t(`${NAME_SPACE}.title`)} accordionDetailsSx={{ padding: 0 }}>
          <Box component="form" onSubmit={methods.handleSubmit(onSubmit)}>
            <SettingsFormContainer onReset={methods.reset} isLoading={isLoading}>
              <SettingsBox
                title={t(`${NAME_SPACE}.dynamic-reactive-power-control.title`)}
                suffix={
                  <Toggle
                    name="dynamicReactiveCurrentControl"
                    disabled={dynamicGridRegenerationSettingsModel.dynamicReactiveCurrentControl.isDisabled}
                    sx={getChangedFieldStyle(dynamicGridRegenerationSettingsModel.dynamicReactiveCurrentControl.changed)}
                  />
                }
                isSingleLine
              />
              <SettingsBox
                title={t(`${NAME_SPACE}.k-factor.title`)}
                suffix={
                  <Box sx={theme => ({ display: 'flex', flexDirection: 'column', gap: 2, [theme.breakpoints.down('sm')]: { width: '100%' } })}>
                    <NumberField
                      name="kFactor.positiveSequenceSystem"
                      label={t(`${NAME_SPACE}.k-factor.positive-sequence-system.label`)}
                      disabled={!canEdit || isFieldDisabled(dynamicGridRegenerationSettingsModel.kFactor.positiveSequenceSystem)}
                      sx={getChangedFieldStyle(dynamicGridRegenerationSettingsModel.kFactor.positiveSequenceSystem.changed)}
                    />
                    <NumberField
                      name="kFactor.negativeSequenceSystem"
                      label={t(`${NAME_SPACE}.k-factor.negative-sequence-system.label`)}
                      disabled={!canEdit || isFieldDisabled(dynamicGridRegenerationSettingsModel.kFactor.negativeSequenceSystem)}
                      sx={getChangedFieldStyle(dynamicGridRegenerationSettingsModel.kFactor.negativeSequenceSystem.changed)}
                    />
                  </Box>
                }
              />
            </SettingsFormContainer>
          </Box>
        </AccordionItem>
      </Grid>
    </FormProvider>
  )
}

export default DynamicGridRegenerationSettings

const getDefaultValues = (dynamicGridRegenerationSettings: DynamicGridRegenerationSettingsModel): DynamicGridRegenerationSettingsForm => ({
  dynamicReactiveCurrentControl: dynamicGridRegenerationSettings.dynamicReactiveCurrentControl.isDisabled,
  kFactor: {
    positiveSequenceSystem: dynamicGridRegenerationSettings.kFactor.positiveSequenceSystem.value,
    negativeSequenceSystem: dynamicGridRegenerationSettings.kFactor.negativeSequenceSystem.value,
  },
})
